/**
 * プラン管理 — where the numbers on every clinic's screen come from.
 *
 * Until this screen existed, plans lived in db/seed.ts: to change what a clinic
 * was allowed, somebody edited a seed file and re-ran it against production.
 * That is why the dashboard could show three different article counts and no
 * screen could say which was authoritative - the limits were not a thing anybody
 * could look at.
 *
 * Two ideas the screen is built around:
 *
 *   THE GRAIN IS PART OF THE NUMBER. 「10」 means nothing on its own. Every field
 *   here says whose ceiling it is - 店舗ごと or クリニック合計 - because
 *   `articles_per_month` has been per store since migration 021 and a plan sold
 *   as "10 articles" to a three-store clinic otherwise quietly costs 30.
 *
 *   AN EDIT REACHES EVERY CLINIC ON THE PLAN, TODAY. So the count of clinics is
 *   on the row before it is opened, the affected clinics are listed inside, and
 *   saving requires step-up re-authentication like every other change that
 *   crosses all tenants at once.
 *
 * A plan is never deleted: subscriptions reference it, and a clinic's history
 * has to keep meaning what it meant. 停止 hides it from new registrations and
 * leaves everyone already on it exactly where they are.
 */
import { useState } from 'react';
import { patch, post, type AdminPlan, type ListResponse, type PlanLimitKey } from '../api.ts';
import {
  derivedTotal,
  draftFrom,
  limitProblem,
  limitsPayload,
  looksDerived,
  microToUsd,
  numberOrUndefined,
  usdToMicro,
  type LimitDraft,
} from '../plan-limits.ts';
import { Card, Empty, Pill, Table, jaDate, jaNumber, limitText, rowOpen } from './ui.tsx';
import { Field } from './EditPanel.tsx';
import { ModalForm } from './Modal.tsx';

const NUMBER_FIELD = { type: 'number', min: 0, step: 1 } as const;

/**
 * The limits, in two tiers.
 *
 * BASIC is what selling a plan actually needs: how much each store may write,
 * how many stores, how many people. Four numbers, and the clinic totals follow
 * from them.
 *
 * ADVANCED is where the exceptions live - a clinic total that is deliberately
 * not the multiplication (a chain buying 20 stores but 100 articles between
 * them), and the vendor spend ceiling, which is denominated in a currency
 * nobody is billed in and is a safety limit rather than a product decision.
 * Folded away, because a form that asks seven questions to sell a plan that
 * needs four is a form somebody fills in wrong.
 */
function LimitFields({
  draft,
  onChange,
  idPrefix,
}: {
  draft: LimitDraft;
  /** One state update per edit: a linked total moves with its per-store field. */
  onChange: (patch: Partial<Record<PlanLimitKey, string>>) => void;
  idPrefix: string;
}) {
  const articleTotal = derivedTotal(draft.articles_per_month ?? '', draft.stores ?? '');
  const imageTotal = derivedTotal(draft.images_per_month ?? '', draft.stores ?? '');
  const articlesLinked = looksDerived(draft, 'articles_per_month', 'clinic_articles_per_month');
  const imagesLinked = looksDerived(draft, 'images_per_month', 'clinic_images_per_month');

  /*
   * Changing a basic number carries its linked total with it.
   *
   * The alternative is a plan whose per-store allowance says 20 and whose total
   * still says 30 because somebody edited one box - two numbers that disagree,
   * which is the whole class of bug this screen was built to end.
   */
  const setLinked = (key: PlanLimitKey, value: string) => {
    const patch: Partial<Record<PlanLimitKey, string>> = { [key]: value };
    const stores = key === 'stores' ? value : (draft.stores ?? '');
    if (articlesLinked) {
      const perStore = key === 'articles_per_month' ? value : (draft.articles_per_month ?? '');
      patch.clinic_articles_per_month = derivedTotal(perStore, stores);
    }
    if (imagesLinked) {
      const perStore = key === 'images_per_month' ? value : (draft.images_per_month ?? '');
      patch.clinic_images_per_month = derivedTotal(perStore, stores);
    }
    onChange(patch);
  };

  return (
    <>
      <Field
        label="記事（1店舗あたり・月）"
        htmlFor={`${idPrefix}-articles_per_month`}
        hint="空欄で無制限。"
      >
        <input
          id={`${idPrefix}-articles_per_month`}
          {...NUMBER_FIELD}
          value={draft.articles_per_month ?? ''}
          placeholder="無制限"
          onChange={(e) => setLinked('articles_per_month', e.target.value)}
        />
      </Field>
      <Field
        label="画像（1店舗あたり・月）"
        htmlFor={`${idPrefix}-images_per_month`}
        hint="空欄で無制限。"
      >
        <input
          id={`${idPrefix}-images_per_month`}
          {...NUMBER_FIELD}
          value={draft.images_per_month ?? ''}
          placeholder="無制限"
          onChange={(e) => setLinked('images_per_month', e.target.value)}
        />
      </Field>
      <Field label="店舗数" htmlFor={`${idPrefix}-stores`} hint="空欄で無制限。">
        <input
          id={`${idPrefix}-stores`}
          {...NUMBER_FIELD}
          value={draft.stores ?? ''}
          placeholder="無制限"
          onChange={(e) => setLinked('stores', e.target.value)}
        />
      </Field>
      <Field label="ユーザー数" htmlFor={`${idPrefix}-seats`} hint="クリニック全体の人数。空欄で無制限。">
        <input
          id={`${idPrefix}-seats`}
          {...NUMBER_FIELD}
          value={draft.seats ?? ''}
          placeholder="無制限"
          onChange={(e) => onChange({ seats: e.target.value })}
        />
      </Field>

      {/*
        What the four numbers above add up to, in words, before anything is
        saved. The clinic total is the figure that refuses a generation when
        every individual store still has room, so it should never be a surprise.
      */}
      <p className="notice" style={{ gridColumn: '1 / -1', marginTop: 0 }}>
        クリニック全体では{' '}
        <strong>
          記事 {limitText(numberOrUndefined(draft.clinic_articles_per_month))} ・ 画像{' '}
          {limitText(numberOrUndefined(draft.clinic_images_per_month))}
        </strong>{' '}
        まで使えます。
        {articlesLinked || imagesLinked ? (
          <span className="muted small">（1店舗あたり × 店舗数で自動計算）</span>
        ) : (
          <span className="muted small">（詳細設定で個別に指定された値）</span>
        )}
      </p>

      <details style={{ gridColumn: '1 / -1' }}>
        <summary className="muted small">詳細設定（通常は変更しません）</summary>
        <div className="editpanel__body" style={{ marginTop: 10 }}>
          <Field
            label="記事（クリニック合計・月）"
            htmlFor={`${idPrefix}-clinic_articles_per_month`}
            hint={
              articlesLinked
                ? `自動計算中（${articleTotal || '無制限'}）。書き換えると固定されます。`
                : '1店舗あたり × 店舗数と一致させると、自動計算に戻ります。'
            }
          >
            <input
              id={`${idPrefix}-clinic_articles_per_month`}
              {...NUMBER_FIELD}
              value={draft.clinic_articles_per_month ?? ''}
              placeholder="無制限"
              onChange={(e) => onChange({ clinic_articles_per_month: e.target.value })}
            />
          </Field>
          <Field
            label="画像（クリニック合計・月）"
            htmlFor={`${idPrefix}-clinic_images_per_month`}
            hint={
              imagesLinked
                ? `自動計算中（${imageTotal || '無制限'}）。書き換えると固定されます。`
                : '1店舗あたり × 店舗数と一致させると、自動計算に戻ります。'
            }
          >
            <input
              id={`${idPrefix}-clinic_images_per_month`}
              {...NUMBER_FIELD}
              value={draft.clinic_images_per_month ?? ''}
              placeholder="無制限"
              onChange={(e) => onChange({ clinic_images_per_month: e.target.value })}
            />
          </Field>
          <Field
            label="AI費用の上限（USD・月）"
            htmlFor={`${idPrefix}-ai_micro_cost_per_month`}
            hint="暴走時の安全弁です。クリニックには表示されません。空欄で無制限。"
            span="wide"
          >
            {/*
              Entered in dollars, stored in micro-USD.
              The column is an integer count of millionths because money never
              floats - but nobody negotiates in millionths, and 5000000 in a box
              is a number people mistype by a factor of ten.
            */}
            <input
              id={`${idPrefix}-ai_micro_cost_per_month`}
              type="number"
              min={0}
              step={0.01}
              value={microToUsd(draft.ai_micro_cost_per_month ?? '')}
              placeholder="無制限"
              onChange={(e) => onChange({ ai_micro_cost_per_month: usdToMicro(e.target.value) })}
            />
          </Field>
        </div>
      </details>
    </>
  );
}

/**
 * The plan catalogue editor, as a section of システム設定.
 *
 * A section rather than a screen of its own because a plan is a system-wide
 * setting in the same sense the model selection is: a number JMC sets once that
 * every clinic then lives inside. It sits beside the model because those two are
 * the settings with a bill attached.
 */
export function PlanSettings({ plans }: { plans: ListResponse<AdminPlan> }) {

  const [creating, setCreating] = useState(false);
  const [newPlan, setNewPlan] = useState({ id: '', name: '', priceJpy: '', description: '' });
  const [newLimits, setNewLimits] = useState<LimitDraft>(() => draftFrom({}));

  const [editing, setEditing] = useState<AdminPlan | null>(null);
  const [editLimits, setEditLimits] = useState<LimitDraft>({});
  const [editPlan, setEditPlan] = useState({ name: '', priceJpy: '', description: '' });

  function openEdit(plan: AdminPlan) {
    setEditing(plan);
    setEditLimits(draftFrom(plan.limits));
    setEditPlan({
      name: plan.name,
      priceJpy: String(plan.price_jpy),
      description: plan.description,
    });
  }

  return (
    <>
      <Card
        title="プラン管理"
        caption="クリニックに割り当てる契約プランと上限値です。変更は同じプランのクリニック全体に即日反映されます。"
        actions={
          <button className="btn btn--sm" type="button" onClick={() => setCreating(true)}>
            プランを作成
          </button>
        }
      >
        {plans.items.length === 0 ? (
          <Empty>プランがありません。</Empty>
        ) : (
          <Table
            head={[
              'プラン',
              '月額',
              '記事（店舗/合計）',
              '画像（店舗/合計）',
              '店舗数',
              '契約中',
              '最終更新',
              '',
            ]}
          >
            {plans.items.map((plan) => (
              <tr key={plan.id} {...rowOpen(() => openEdit(plan))}>
                <td>
                  <strong>{plan.name}</strong>
                  <span className="muted small"> {plan.id}</span>
                  {!plan.is_active && (
                    <>
                      {' '}
                      <Pill tone="muted">停止中</Pill>
                    </>
                  )}
                  {plan.description && <div className="muted small">{plan.description}</div>}
                </td>
                <td>{jaNumber(plan.price_jpy)}円</td>
                {/*
                  Both grains in one cell, always in the same order, because the
                  question this screen exists to answer is "10 of what".
                */}
                <td>
                  {limitText(plan.limits.articles_per_month)} /{' '}
                  {limitText(plan.limits.clinic_articles_per_month)}
                </td>
                <td>
                  {limitText(plan.limits.images_per_month)} /{' '}
                  {limitText(plan.limits.clinic_images_per_month)}
                </td>
                <td>{limitText(plan.limits.stores)}</td>
                <td>{jaNumber(plan.clinics)}院</td>
                <td className="muted small">{jaDate(plan.updated_at)}</td>
                <td>
                  <button className="btn btn--sm btn--ghost" type="button" onClick={() => openEdit(plan)}>
                    編集
                  </button>
                </td>
              </tr>
            ))}
          </Table>
        )}
        <p className="muted small">
          「店舗ごと」は各店舗が使える数、「クリニック合計」は全店舗の合計上限です。空欄は無制限を意味します。
        </p>
      </Card>

      <ModalForm
        open={creating}
        onClose={() => setCreating(false)}
        title="プランを作成"
        caption="IDは請求やURLに使う英数字の識別子です。あとから変更できません。"
        submitLabel="作成する"
        onSubmit={async () => {
          const problem = limitProblem(newLimits);
          if (problem) throw new Error(problem);
          await post('/admin/plans', {
            id: newPlan.id.trim(),
            name: newPlan.name.trim(),
            priceJpy: Number(newPlan.priceJpy || 0),
            description: newPlan.description.trim(),
            limits: limitsPayload(newLimits),
          });
          setNewPlan({ id: '', name: '', priceJpy: '', description: '' });
          setNewLimits(draftFrom({}));
        }}
      >
        <Field label="プランID" htmlFor="plan-id" required hint="小文字英数字・ハイフン。例: starter">
          <input
            id="plan-id"
            value={newPlan.id}
            placeholder="starter"
            onChange={(e) => setNewPlan({ ...newPlan, id: e.target.value })}
          />
        </Field>
        <Field label="プラン名" htmlFor="plan-name" required>
          <input
            id="plan-name"
            maxLength={80}
            value={newPlan.name}
            placeholder="スタータープラン"
            onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })}
          />
        </Field>
        <Field label="月額（円）" htmlFor="plan-price" required>
          <input
            id="plan-price"
            type="number"
            min={0}
            value={newPlan.priceJpy}
            onChange={(e) => setNewPlan({ ...newPlan, priceJpy: e.target.value })}
          />
        </Field>
        <Field label="メモ" htmlFor="plan-desc" hint="JMC内部向け。クリニックには表示されません。" span="full">
          <input
            id="plan-desc"
            maxLength={500}
            value={newPlan.description}
            onChange={(e) => setNewPlan({ ...newPlan, description: e.target.value })}
          />
        </Field>
        <LimitFields
          draft={newLimits}
          idPrefix="new"
          onChange={(patch) => setNewLimits({ ...newLimits, ...patch })}
        />
      </ModalForm>

      <ModalForm
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing ? `${editing.name} を編集` : 'プランを編集'}
        caption={
          editing && editing.clinics > 0
            ? `契約中の${editing.clinics}院に即日反映されます。`
            : '契約中のクリニックはありません。'
        }
        onSubmit={async () => {
          if (!editing) return;
          const problem = limitProblem(editLimits);
          if (problem) throw new Error(problem);
          await patch(`/admin/plans/${editing.id}`, {
            name: editPlan.name.trim(),
            priceJpy: Number(editPlan.priceJpy || 0),
            description: editPlan.description.trim(),
            isActive: editing.is_active,
            limits: limitsPayload(editLimits),
          });
        }}
      >
        <Field label="プラン名" htmlFor="edit-name" required>
          <input
            id="edit-name"
            maxLength={80}
            value={editPlan.name}
            onChange={(e) => setEditPlan({ ...editPlan, name: e.target.value })}
          />
        </Field>
        <Field label="月額（円）" htmlFor="edit-price" required>
          <input
            id="edit-price"
            type="number"
            min={0}
            value={editPlan.priceJpy}
            onChange={(e) => setEditPlan({ ...editPlan, priceJpy: e.target.value })}
          />
        </Field>
        <Field label="メモ" htmlFor="edit-desc" span="full">
          <input
            id="edit-desc"
            maxLength={500}
            value={editPlan.description}
            onChange={(e) => setEditPlan({ ...editPlan, description: e.target.value })}
          />
        </Field>
        <LimitFields
          draft={editLimits}
          idPrefix="edit"
          onChange={(patch) => setEditLimits({ ...editLimits, ...patch })}
        />
        {editing && (
          <Field label="提供状態" htmlFor="edit-active" span="full" hint="停止すると新規登録の選択肢から外れます。契約中のクリニックはそのままです。">
            <label className="check">
              <input
                id="edit-active"
                type="checkbox"
                checked={editing.is_active}
                onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })}
              />
              このプランを提供中にする
            </label>
          </Field>
        )}
      </ModalForm>
    </>
  );
}
